/**
 * Phaser grouped tag filters (prototype gate).
 *
 * snippets/phaser-tag-groups.liquid always renders the grouped tag filters
 * inside disabled + hidden <fieldset data-phaser-grouped-filters> wrappers, so
 * they do nothing on the live store. This module reveals and enables them only
 * when localStorage 'phaserGroupedFilters' === '1'.
 *   ?filters=grouped  turns it on (remembered in this browser)
 *   ?filters=off      turns it off
 *
 * The fieldsets carry data-skip-node-update, so the facets morph keeps their
 * live (enabled) attributes; a MutationObserver catches any fresh ones the
 * section re-render inserts.
 *
 * Each group is single-select (the S&D Tags filter uses AND logic, so two
 * values from one group usually return nothing). Ticking a value unticks the
 * rest of its group before facets.js reads the form. Groups marked data-multi
 * keep normal checkbox behaviour.
 */

const STORAGE_KEY = 'phaserGroupedFilters';
const GATE_ATTRIBUTE = 'data-phaser-grouped-filters';
const GATED_SELECTOR = `fieldset[${GATE_ATTRIBUTE}]`;

/**
 * @returns {boolean}
 */
const readFlag = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('filters');

    if (mode === 'grouped') localStorage.setItem(STORAGE_KEY, '1');
    if (mode === 'off') localStorage.removeItem(STORAGE_KEY);

    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * @param {ParentNode} root
 */
const reveal = (root) => {
  for (const fieldset of root.querySelectorAll(GATED_SELECTOR)) {
    if (!(fieldset instanceof HTMLFieldSetElement)) continue;
    if (fieldset.disabled) fieldset.disabled = false;
    if (fieldset.hidden) fieldset.hidden = false;
  }
};

/**
 * Single-select per group. Runs in the capture phase on window, so it fires
 * before the component framework's document-level on:change handler that
 * calls facets.js updateFilters().
 * @param {Event} event
 */
const onChange = (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox' || !input.checked) return;

  const group = input.closest('fieldset[data-phaser-tag-group]');
  if (!group || group.hasAttribute('data-multi')) return;

  for (const other of group.querySelectorAll('input[type="checkbox"]:checked')) {
    if (other !== input && other instanceof HTMLInputElement) other.checked = false;
  }
};

if (readFlag()) {
  document.documentElement.setAttribute(GATE_ATTRIBUTE, '');
  reveal(document);

  window.addEventListener('change', onChange, { capture: true });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length > 0) {
        reveal(document);
        return;
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
} else {
  document.documentElement.removeAttribute(GATE_ATTRIBUTE);
}
